/**
 * Ressourcen in der Belegung (Board „Belegung · Ressourcen“): Tagwahl, Tagesraster mit Ziehen zum
 * Markieren und dunkler Leiste, darunter die Tagesliste je Ressource mit Text statt nur Farbe. In
 * schmaler Karte (Handy) nur die Karten je Ressource mit „Zeit sperren“.
 */
import { EmptyState } from '@/shared/components';
import { useContainerQuery } from '@/shared/hooks/useContainerQuery';
import { ResourceBlockSheet } from './ResourceBlockSheet';
import { ResourceCancelDialog } from './ResourceCancelDialog';
import { ResourceDayHeader } from './ResourceDayHeader';
import { ResourceDayRow } from './ResourceDayRow';
import { ResourceGrid } from './ResourceGrid';
import { gridAxis } from './resourceGridModel';
import { ResourceLoadState } from './ResourceLoadState';
import { ResourceSelectionBar } from './ResourceSelectionBar';
import { useResourceCalendar } from './useResourceCalendar';

export function ResourceCalendarSection({
  groupId,
  dates,
  today,
  canManage,
  day,
  onDay,
}: {
  groupId: string;
  dates: string[];
  today: string;
  canManage: boolean;
  /** Tag aus der Adresse (`?day=`); ohne ihn merkt sich die Karte den Tag selbst. */
  day?: string;
  onDay?: (day: string) => void;
}) {
  const state = useResourceCalendar({ groupId, dates, today, day, onDay });
  const wide = useContainerQuery(720);
  const data = state.occupancy.data;
  const axis = data ? gridAxis(data.resources) : null;
  const first = state.active[0];

  return (
    <div ref={wide.ref} role="region" aria-label="Belegung der Ressourcen" className="relative">
      <ResourceLoadState query={state.occupancy}>
        {data && data.resources.length === 0 ? (
          <EmptyState
            icon="Building"
            title="Keine Ressourcen"
            description="Dieses Objekt hat noch keine Ressource."
          />
        ) : data ? (
          <>
            <ResourceDayHeader
              days={state.days}
              day={state.day}
              timezone={data.timezone}
              canDrag={wide.matches && canManage && axis !== null}
              onDay={state.chooseDay}
            />
            {state.error && !state.blocking && !state.cancelling && (
              <p role="alert" className="text-sealwax px-3 pt-3 text-sm md:px-[18px]">
                {state.error}
              </p>
            )}
            {wide.matches && axis && (
              <>
                <ResourceGrid
                  axis={axis}
                  rows={data.resources}
                  date={state.day}
                  canManage={canManage}
                  selection={state.selection}
                  onSelect={state.select}
                  onCancel={state.askCancel}
                />
                {canManage && first && (
                  <ResourceSelectionBar
                    selection={state.selection}
                    resourceName={state.selectionName}
                    onClear={state.clearSelection}
                    onBlock={() =>
                      state.selection
                        ? state.openBlock(
                            state.selection.resourceId,
                            state.selection.startMinute,
                            state.selection.endMinute,
                          )
                        : state.openBlock(first.resourceId)
                    }
                  />
                )}
              </>
            )}
            <div className="flex flex-col gap-3 p-3 md:p-[18px]">
              {data.resources.map((row) => (
                <ResourceDayRow
                  key={row.resourceId}
                  row={row}
                  date={state.day}
                  strip={!wide.matches || !axis}
                  canManage={canManage}
                  busyId={state.cancel.isPending ? (state.cancel.variables?.id ?? null) : null}
                  timezone={data.timezone}
                  onBlock={() => state.openBlock(row.resourceId)}
                  onCancel={state.askCancel}
                />
              ))}
            </div>
          </>
        ) : null}
      </ResourceLoadState>
      {state.blocking && data && (
        <ResourceBlockSheet
          rows={state.active}
          resourceId={state.blocking.resourceId}
          initialStart={state.blocking.start}
          initialEnd={state.blocking.end}
          date={state.day}
          dates={state.days}
          timezone={data.timezone}
          saving={state.block.isPending}
          error={state.error}
          onSubmit={(value) => state.block.mutate(value)}
          onClose={state.closeBlock}
        />
      )}
      <ResourceCancelDialog
        entry={state.cancelling}
        loading={state.cancel.isPending}
        error={state.cancelling ? state.error : null}
        onClose={state.closeCancel}
        onConfirm={state.confirmCancel}
      />
    </div>
  );
}
